import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigProvider, Spin, notification, theme } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  IoArrowBackOutline,
  IoBeakerOutline,
  IoCloseCircleOutline,
  IoCubeOutline,
  IoDiceOutline,
  IoFlameOutline,
  IoGameControllerOutline,
  IoLayersOutline,
  IoMedalOutline,
  IoPodiumOutline,
  IoRefreshOutline,
  IoSparklesOutline,
  IoStarOutline,
  IoStatsChartOutline,
  IoStopwatchOutline,
  IoSyncOutline,
  IoTimeOutline,
  IoTrophyOutline,
  IoTrendingUpOutline,
} from "react-icons/io5";
import { normalizeLanguage, translatePair } from "../../services/language";
import { setLanguage as setI18nLanguage, t as tKey } from "../../i18n";
import { useAuth } from "../../services/AuthContext";

const STATS_HISTORY_PAGE_SIZE = 100;
const GAME_STATS_MODE_KEYS = ["ciencia", "cienzar", "sciencevsazar", "azar"];

const formatDurationLabel = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatLongDurationLabel = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return formatDurationLabel(safeSeconds);
};

const formatMetricValue = (value, decimals = 2) =>
  Number(Math.max(0, Number(value) || 0)).toFixed(decimals);

const formatCimaxScore = (value) => String(Math.round(Math.max(0, Number(value) || 0)));

const formatStatsFavoriteModeLabel = (value) => {
  const raw = String(value || "").trim();
  const normalized = raw.toLowerCase();
  if (
    normalized.includes("sciencevsazar") ||
    normalized.includes("science v/s azar") ||
    normalized.includes("science-vs-azar") ||
    normalized.includes("ciencia v/s azar") ||
    normalized.includes("cienciavsazar") ||
    normalized.includes("contrazar")
  ) {
    return "CONTRAZAR";
  }
  return raw || "-";
};

const normalizeIdentity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const toMetricNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveStatsModeField = (mode) => {
  const normalized = String(mode || "")
    .trim()
    .toLowerCase();

  if (
    normalized.includes("sciencevsazar") ||
    normalized.includes("science-vs-azar") ||
    normalized.includes("cienciavsazar") ||
    normalized.includes("contrazar")
  ) {
    return "sciencevsazar";
  }
  if (normalized.includes("cienzar")) return "cienzar";
  if (normalized.includes("free")) return "ciencia";
  if (normalized.includes("dice")) return "azar";

  if (normalized === "ciencia" || normalized === "science") return "ciencia";
  if (normalized === "azar" || normalized === "dice") return "azar";

  return "";
};

const resolveOwnMatchForceMetric = (participantStat) => {
  if (!participantStat || typeof participantStat !== "object") return 0;
  return Math.max(
    0,
    toMetricNumber(participantStat.maxGameForce),
    toMetricNumber(participantStat.maxForce),
    toMetricNumber(participantStat.forceMax),
    toMetricNumber(participantStat.peakForce),
    toMetricNumber(participantStat.highestForce),
    toMetricNumber(participantStat.force)
  );
};

const resolveTotalMatchForceMetric = (entry) => {
  const participantStats = Array.isArray(entry?.participantStats) ? entry.participantStats : [];
  const summedParticipantForce = participantStats.reduce((sum, item) => {
    const forceValue = Math.max(
      0,
      toMetricNumber(item?.maxGameForce),
      toMetricNumber(item?.maxForce),
      toMetricNumber(item?.forceMax),
      toMetricNumber(item?.peakForce),
      toMetricNumber(item?.highestForce),
      toMetricNumber(item?.force)
    );
    return sum + forceValue;
  }, 0);

  return Math.max(
    0,
    summedParticipantForce,
    toMetricNumber(entry?.totalGameForceAllPlayers),
    toMetricNumber(entry?.totalMatchForce),
    toMetricNumber(entry?.maxTotalForce),
    toMetricNumber(entry?.levelPlayTotal),
    toMetricNumber(entry?.levelplay)
  );
};

const resolveMatchPlayerCount = (entry) => {
  const byParticipants = Array.isArray(entry?.participants) ? entry.participants.length : 0;
  const byMemberSize = Math.floor(toMetricNumber(entry?.memberSize));
  return Math.max(1, byMemberSize || byParticipants || 2);
};

const resolveOwnMatchPotencyMetric = (participantStat, entry) => {
  if (!participantStat || typeof participantStat !== "object") return 0;

  const directPotencyMetric = Math.max(
    0,
    toMetricNumber(participantStat.potency),
    toMetricNumber(participantStat.gamePotency),
    toMetricNumber(participantStat.maxPotency),
    toMetricNumber(participantStat.peakPotency),
    toMetricNumber(participantStat.highestPotency)
  );

  if (directPotencyMetric > 0) return directPotencyMetric;

  const forceGame = resolveOwnMatchForceMetric(participantStat);
  if (forceGame <= 0) return 0;

  const turns = Math.max(
    1,
    Math.floor(
      Math.max(
        1,
        toMetricNumber(participantStat.totalMoves),
        toMetricNumber(participantStat.totalTurns),
        toMetricNumber(participantStat.turns)
      )
    )
  );
  const timeTotalMs = Math.max(
    1,
    toMetricNumber(participantStat.timeTotalMs) ||
      toMetricNumber(participantStat.usedTimeMs) ||
      toMetricNumber(entry?.timeTotalMs) ||
      Math.round(Math.max(1, toMetricNumber(entry?.havingTime)) * 60 * 1000)
  );
  const fallbackUsedMs = Math.round(
    (Math.max(0, toMetricNumber(entry?.durationSeconds)) * 1000) /
      resolveMatchPlayerCount(entry)
  );
  const timeUsedMs = Math.min(
    timeTotalMs,
    toMetricNumber(participantStat.timeUsedMs) ||
      toMetricNumber(participantStat.usedTimeMs) ||
      toMetricNumber(entry?.timeUsedMs) ||
      fallbackUsedMs
  );
  const effectiveTime = Math.min(8, timeUsedMs / timeTotalMs);
  const denominator = Math.pow(2, effectiveTime) * turns;
  return denominator > 0 ? forceGame / denominator : forceGame;
};

const resolveSummaryPlayerRole = (summary, playerStatEntry) => {
  if (!summary || !playerStatEntry) return "other";
  const normalizedPlayerId = normalizeIdentity(
    playerStatEntry?.userIdNormalized || playerStatEntry?.userId
  );
  if (!normalizedPlayerId) return "other";

  const winnerId = normalizeIdentity(summary?.winnerIdNormalized || summary?.winnerId);
  const loserId = normalizeIdentity(summary?.loserIdNormalized || summary?.loserId);
  if (!winnerId || !loserId) return "other";

  if (normalizedPlayerId === winnerId) return "winner";
  if (normalizedPlayerId === loserId) return "loser";
  return "other";
};

const resolveSummaryPlayerRoleByCandidates = (summary, identityCandidates = new Set()) => {
  const candidates = identityCandidates instanceof Set ? identityCandidates : new Set();
  if (!summary || candidates.size === 0) return "other";
  const winnerId = normalizeIdentity(summary?.winnerIdNormalized || summary?.winnerId);
  const loserId = normalizeIdentity(summary?.loserIdNormalized || summary?.loserId);

  if (winnerId && candidates.has(winnerId)) return "winner";
  if (loserId && candidates.has(loserId)) return "loser";
  return "other";
};

const resolveSummaryMatchForce = (summary, role) => {
  if (role === "winner") return Math.max(0, toMetricNumber(summary?.winnerForce));
  if (role === "loser") return Math.max(0, toMetricNumber(summary?.loserForce));
  return 0;
};

const resolveSummaryMatchPotency = (summary, role) => {
  if (role === "winner") return Math.max(0, toMetricNumber(summary?.winnerPotency));
  if (role === "loser") return Math.max(0, toMetricNumber(summary?.loserPotency));
  return 0;
};

const resolveSummaryMatchLevel = (summary) => Math.max(0, toMetricNumber(summary?.levelGame));

const computeGameStatsFromMatches = (matches = [], identityCandidates = new Set()) => {
  const candidateSet = identityCandidates instanceof Set ? identityCandidates : new Set();
  const isCurrentUserIdentity = (participant) => {
    if (!participant || typeof participant !== "object") return false;
    if (participant?.isCurrentUser === true) return true;

    const normalizedParticipantId = normalizeIdentity(
      participant?.userIdNormalized || participant?.userId
    );
    if (!normalizedParticipantId) return false;
    if (candidateSet.has(normalizedParticipantId)) return true;

    if (normalizedParticipantId.includes("@")) {
      const localAlias = normalizeIdentity(normalizedParticipantId.split("@")[0]);
      if (localAlias && candidateSet.has(localAlias)) return true;
    }

    return false;
  };

  const buckets = GAME_STATS_MODE_KEYS.reduce((acc, modeKey) => {
    acc[modeKey] = {
      matches: 0,
      forceSum: 0,
      levelSum: 0,
      potencySum: 0,
      maxForce: 0,
      maxLevel: 0,
      maxPotency: 0,
    };
    return acc;
  }, {});

  Array.isArray(matches) &&
    matches.forEach((match) => {
      const modeKey = resolveStatsModeField(match?.mode);
      if (!modeKey) return;

      const bucket = buckets[modeKey];
      if (!bucket) return;

      const participants = Array.isArray(match?.participants) ? match.participants : [];
      const currentPlayer =
        participants.find(isCurrentUserIdentity) ||
        participants.find((participant) =>
          (() => {
            const normalized = normalizeIdentity(participant?.userIdNormalized || participant?.userId);
            if (!normalized) return false;
            if (candidateSet.has(normalized)) return true;
            if (normalized.includes("@")) {
              const localAlias = normalizeIdentity(normalized.split("@")[0]);
              return Boolean(localAlias && candidateSet.has(localAlias));
            }
            return false;
          })()
        ) ||
        null;
      const cimaxSummary = match?.cimaxSummary && typeof match.cimaxSummary === "object"
        ? match.cimaxSummary
        : null;
      const currentPlayerId = String(
        currentPlayer?.userIdNormalized || currentPlayer?.userId || ""
      ).trim();
      const participantStats = Array.isArray(match?.participantStats)
        ? match.participantStats
        : [];

      const participantForCurrentUser = currentPlayerId
        ? participantStats.find(
            (entry) =>
              String(entry?.userIdNormalized || entry?.userId || "").trim() === currentPlayerId
          )
        : null;
      const participantForCurrentUserByCandidates = currentPlayerId
        ? null
        : participantStats.find((entry) => {
            const normalizedParticipantId = normalizeIdentity(
              entry?.userIdNormalized || entry?.userId
            );
            if (!normalizedParticipantId) return false;
            if (candidateSet.has(normalizedParticipantId)) return true;
            if (normalizedParticipantId.includes("@")) {
              const localAlias = normalizeIdentity(normalizedParticipantId.split("@")[0]);
              return Boolean(localAlias && candidateSet.has(localAlias));
            }
            return false;
          });
      const fallbackParticipant = participantForCurrentUser || null;
      const roleFromPlayer = resolveSummaryPlayerRole(cimaxSummary, fallbackParticipant || currentPlayer);
      const roleFromCandidates = resolveSummaryPlayerRoleByCandidates(
        cimaxSummary,
        candidateSet
      );
      const playerRoleInSummary = roleFromPlayer !== "other" ? roleFromPlayer : roleFromCandidates;

      const resolvedParticipantForCurrentUser =
        fallbackParticipant || participantForCurrentUserByCandidates || null;
      const ownMatchForce = resolveOwnMatchForceMetric(resolvedParticipantForCurrentUser);
      const ownMatchPotency = resolveOwnMatchPotencyMetric(
        resolvedParticipantForCurrentUser,
        match
      );
      const summaryTotalMatchForce = resolveSummaryMatchLevel(cimaxSummary);

      const ownMatchForceFromSummary = resolveSummaryMatchForce(cimaxSummary, playerRoleInSummary);
      const ownMatchPotencyFromSummary = resolveSummaryMatchPotency(
        cimaxSummary,
        playerRoleInSummary
      );

      const resolvedOwnMatchForce = resolvedParticipantForCurrentUser
        ? Math.max(ownMatchForce, ownMatchForceFromSummary)
        : ownMatchForceFromSummary;
      const resolvedOwnMatchPotency = resolvedParticipantForCurrentUser
        ? Math.max(ownMatchPotency, ownMatchPotencyFromSummary)
        : ownMatchPotencyFromSummary;

      const totalMatchForce = resolveTotalMatchForceMetric(match) || summaryTotalMatchForce;

      bucket.matches += 1;
      if (resolvedParticipantForCurrentUser || ownMatchForceFromSummary > 0) {
        bucket.forceSum += resolvedOwnMatchForce;
        bucket.maxForce = Math.max(bucket.maxForce, resolvedOwnMatchForce);

        bucket.potencySum += resolvedOwnMatchPotency;
        bucket.maxPotency = Math.max(bucket.maxPotency, resolvedOwnMatchPotency);
      }

      bucket.levelSum += totalMatchForce;
      bucket.maxLevel = Math.max(bucket.maxLevel, totalMatchForce);
    });

  return GAME_STATS_MODE_KEYS.reduce((acc, modeKey) => {
    const source = buckets[modeKey];
    if (!source) return acc;

    acc[modeKey] = {
      mode: modeKey,
      matches: Math.max(0, Math.floor(source.matches)),
      maxGameForceAchieved: Number(source.maxForce.toFixed(2)),
      averageGameForce: Number(
        source.matches > 0
          ? (source.forceSum / source.matches).toFixed(2)
          : "0"
      ),
      maxGameLevelAchieved: Number(source.maxLevel.toFixed(2)),
      averageGameLevel: Number(
        source.matches > 0
          ? (source.levelSum / source.matches).toFixed(2)
          : "0"
      ),
      maxGamePotency: Number(source.maxPotency.toFixed(2)),
      averageGamePotency: Number(
        source.matches > 0
          ? (source.potencySum / source.matches).toFixed(2)
          : "0"
      ),
    };
    return acc;
  }, {});
};

const buildIdentityCandidates = (source = {}) => {
  const candidates = new Set();
  const pushCandidate = (value) => {
    const normalized = normalizeIdentity(value);
    if (!normalized) return;
    candidates.add(normalized);
    if (normalized.includes("@")) {
      const localPart = normalizeIdentity(normalized.split("@")[0]);
      if (localPart) candidates.add(localPart);
    }
  };

  pushCandidate(source?.email);
  pushCandidate(source?.loginIdentifier);
  pushCandidate(source?.username);
  pushCandidate(source?.userName);
  pushCandidate(source?.id);
  pushCandidate(source?._id);
  pushCandidate(source?.uid);
  pushCandidate(source?.sub);
  return candidates;
};

const computeTournamentWinsByModeFromLeaderboard = (payload, identityCandidates) => {
  const candidates =
    identityCandidates instanceof Set ? identityCandidates : new Set();
  const modes = payload?.modes && typeof payload.modes === "object" ? payload.modes : {};

  const resolveWins = (modeKey) => {
    const players = Array.isArray(modes?.[modeKey]) ? modes[modeKey] : [];
    let wins = 0;
    for (let index = 0; index < players.length; index += 1) {
      const player = players[index];
      const normalizedUserId = normalizeIdentity(player?.userId);
      const normalizedLocal =
        normalizedUserId && normalizedUserId.includes("@")
          ? normalizeIdentity(normalizedUserId.split("@")[0])
          : "";
      if (!normalizedUserId && !normalizedLocal) continue;
      if (!candidates.has(normalizedUserId) && !candidates.has(normalizedLocal)) continue;
      wins = Math.max(wins, Math.max(0, Number(player?.wins) || 0));
    }
    return wins;
  };

  const ciencia = resolveWins("ciencia");
  const cienzar = resolveWins("cienzar");
  const sciencevsazar = resolveWins("sciencevsazar");
  const azar = resolveWins("azar");
  return {
    ciencia,
    cienzar,
    sciencevsazar,
    azar,
    total: ciencia + cienzar + sciencevsazar + azar,
  };
};

const Stats = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { language: outletLanguage = "es" } = useOutletContext() || {};
  const language = normalizeLanguage(outletLanguage);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [leaderboardTournamentWins, setLeaderboardTournamentWins] = useState({
    ciencia: 0,
    cienzar: 0,
    sciencevsazar: 0,
    azar: 0,
    total: 0,
  });
  const t = useCallback((es, en) => translatePair(language, es, en), [language]);
  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);

  const identityCandidates = useMemo(() => buildIdentityCandidates(user || {}), [user]);

const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const initialResponse = await axios.get("/tournaments/history/me", {
        params: { page: 1, pageSize: STATS_HISTORY_PAGE_SIZE },
      });
      const firstPageMatches = Array.isArray(initialResponse?.data?.matches)
        ? initialResponse.data.matches
        : [];
      const matches = [...firstPageMatches];
      const totalRaw = Number(initialResponse?.data?.total);
      const serverPageSize = Math.max(
        1,
        Math.floor(
          Number(initialResponse?.data?.pageSize) || Math.max(1, STATS_HISTORY_PAGE_SIZE)
        )
      );
      const hasServerTotal = Number.isFinite(totalRaw) && totalRaw >= 0;

      if (hasServerTotal) {
        const totalPages = Math.max(1, Math.ceil(totalRaw / serverPageSize));
        for (let page = 2; page <= totalPages; page += 1) {
          const pageResponse = await axios.get("/tournaments/history/me", {
            params: { page, pageSize: STATS_HISTORY_PAGE_SIZE },
          });
          const nextMatches = Array.isArray(pageResponse?.data?.matches)
            ? pageResponse.data.matches
            : [];
          matches.push(...nextMatches);
        }
      } else {
        for (let page = 2; page <= 2000; page += 1) {
          const pageResponse = await axios.get("/tournaments/history/me", {
            params: { page, pageSize: STATS_HISTORY_PAGE_SIZE },
          });
          const nextMatches = Array.isArray(pageResponse?.data?.matches)
            ? pageResponse.data.matches
            : [];
          matches.push(...nextMatches);
          if (!nextMatches.length) break;
        }
      }

      const sourceStats = initialResponse?.data?.stats || null;
      const gameStatsByMode = computeGameStatsFromMatches(matches, identityCandidates);
      setStats(sourceStats ? { ...sourceStats, gameStatsByMode } : { gameStatsByMode });
    } catch (error) {
      notification.error({
        message: t("Error de estadisticas", "Stats Error"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t("No se pudieron cargar las estadisticas.", "Could not load stats."),
      });
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [identityCandidates, t]);

  useEffect(() => {
    loadStats().catch((error) => console.log(error));
  }, [loadStats]);

  const loadLeaderboardTournamentWins = useCallback(async () => {
    if (!(identityCandidates instanceof Set) || identityCandidates.size === 0) {
      setLeaderboardTournamentWins({
        ciencia: 0,
        cienzar: 0,
        sciencevsazar: 0,
        azar: 0,
        total: 0,
      });
      return;
    }

    try {
      const response = await axios.get("/tournaments/leaderboard/modes");
      const computed = computeTournamentWinsByModeFromLeaderboard(
        response?.data,
        identityCandidates
      );
      setLeaderboardTournamentWins(computed);
    } catch (error) {
      // Silencioso: el backend de historial sigue siendo la fuente principal.
    }
  }, [identityCandidates]);

  useEffect(() => {
    loadLeaderboardTournamentWins().catch((error) => console.log(error));
  }, [loadLeaderboardTournamentWins]);

  const tournamentWinsByMode = useMemo(() => {
    const fromStats = stats?.tournamentWinsByMode || {};
    const ciencia = Math.max(
      0,
      Number(fromStats?.ciencia) || 0,
      Number(leaderboardTournamentWins?.ciencia) || 0
    );
    const cienzar = Math.max(
      0,
      Number(fromStats?.cienzar) || 0,
      Number(leaderboardTournamentWins?.cienzar) || 0
    );
    const sciencevsazar = Math.max(
      0,
      Number(fromStats?.sciencevsazar) || 0,
      Number(fromStats?.contrazar) || 0,
      Number(leaderboardTournamentWins?.sciencevsazar) || 0
    );
    const azar = Math.max(
      0,
      Number(fromStats?.azar) || 0,
      Number(leaderboardTournamentWins?.azar) || 0
    );
    return {
      ciencia,
      cienzar,
      sciencevsazar,
      azar,
      total: ciencia + cienzar + sciencevsazar + azar,
    };
  }, [stats, leaderboardTournamentWins]);

  const cards = useMemo(
    () => [
      {
        key: "matches",
        label: t("Partidas", "Matches"),
        value: Number(stats?.totalMatches || 0),
        className: "border-[#25508c] bg-[#0d2344]",
        labelClass: "text-[#8cc6ff]",
      },
      {
        key: "wins",
        label: t("Victorias", "Wins"),
        value: Number(stats?.wins || 0),
        className: "border-[#2b8b4e] bg-[#13372a]",
        labelClass: "text-[#86ffb5]",
      },
      {
        key: "losses",
        label: t("Derrotas", "Losses"),
        value: Number(stats?.losses || 0),
        className: "border-[#8b2b2b] bg-[#3b1a1a]",
        labelClass: "text-[#ff9f9f]",
      },
      {
        key: "avg",
        label: t("Tiempo medio", "Avg duration"),
        value: formatDurationLabel(stats?.averageDurationSeconds || 0),
        className: "border-[#5c4ab6] bg-[#1f2350]",
        labelClass: "text-[#bab3ff]",
      },
      {
        key: "totalTime",
        label: t("Tiempo total jugado", "Total play time"),
        value: formatLongDurationLabel(stats?.totalTimePlayedSeconds || 0),
        className: "border-[#336b7a] bg-[#112f3a]",
        labelClass: "text-[#9cecff]",
      },
      {
        key: "fav",
        label: t("Modo favorito", "Favorite mode"),
        value: formatStatsFavoriteModeLabel(
          stats?.favoriteModeLabel || stats?.favoriteMode || "-"
        ).toUpperCase(),
        extra: `${t("Win rate", "Win rate")}: ${Number(stats?.winRate || 0).toFixed(1)}%`,
        className: "border-[#7a5a1f] bg-[#3a2a12]",
        labelClass: "text-[#ffe09a]",
      },
      {
        key: "currentStreak",
        label: t("Racha de victorias", "Win streak"),
        value: Number(stats?.currentWinStreak || 0),
        className: "border-[#2f8b76] bg-[#14392f]",
        labelClass: "text-[#9fffd8]",
      },
      {
        key: "bestStreak",
        label: t("Mejor racha", "Best streak"),
        value: Number(stats?.bestWinStreak || 0),
        className: "border-[#4f7d2f] bg-[#293a14]",
        labelClass: "text-[#cfff9f]",
      },
      {
        key: "capturedBlocks",
        label: t("Bloques capturados", "Captured blocks"),
        value: Number(stats?.capturedBlocks || 0),
        className: "border-[#8b5a2b] bg-[#3b2613]",
        labelClass: "text-[#ffc99f]",
      },
      {
        key: "capturedSpecial",
        label: t("Especiales capturadas", "Special captures"),
        value: Number(stats?.specialCaptures || 0),
        className: "border-[#7a2b8b] bg-[#35113e]",
        labelClass: "text-[#f4b0ff]",
      },
      {
        key: "dailyStreak",
        label: t("Racha de dias", "Day streak"),
        value: Number(stats?.consecutiveDaysStreak || 0),
        extra: t("Dias seguidos jugando", "Consecutive days playing"),
        className: "border-[#2b5f8b] bg-[#112a3f]",
        labelClass: "text-[#9fd7ff]",
      },
    ],
    [stats, t]
  );

  const tournamentWinsCards = useMemo(
    () => [
      {
        key: "science",
        label: t("Torneos ganados Ciencia", "Science tournaments won"),
        value: Number(tournamentWinsByMode?.ciencia || 0),
        className: "border-[#3c76c7] bg-[#102a4d]",
        labelClass: "text-[#bfe1ff]",
      },
      {
        key: "cienzar",
        label: t("Torneos ganados Cienzar", "Cienzar tournaments won"),
        value: Number(tournamentWinsByMode?.cienzar || 0),
        className: "border-[#2f9658] bg-[#123a27]",
        labelClass: "text-[#bfffd7]",
      },
      {
        key: "sciencevsazar",
        label: t("Torneos ganados Contrazar", "Contrazar tournaments won"),
        value: Number(tournamentWinsByMode?.sciencevsazar || 0),
        className: "border-[#b9414c] bg-[#3d1018]",
        labelClass: "text-[#ffc2ca]",
      },
      {
        key: "dice",
        label: t("Torneos ganados Azar", "Dice tournaments won"),
        value: Number(tournamentWinsByMode?.azar || 0),
        className: "border-[#b98a1f] bg-[#3f2d11]",
        labelClass: "text-[#ffe3a6]",
      },
    ],
    [tournamentWinsByMode, t]
  );

  const modeColumns = useMemo(() => {
    const modeStatsByApi = stats?.gameStatsByMode || {};
    return [
      {
        key: "ciencia",
        title: t("Modo Ciencia", "Science mode"),
        cimaxLabel: t("Puntuacion CIMAX Ciencia", "CIMAX Science score"),
        cimaxScore: formatCimaxScore(stats?.cimaxScores?.ciencia || 0),
        wrapperClass: "border-[#3c76c7] bg-[#0f2646]",
        titleClass: "text-[#bfe1ff]",
        metricLabelClass: "text-[#93c9ff]",
      },
      {
        key: "cienzar",
        title: t("Modo Cienzar", "Cienzar mode"),
        cimaxLabel: t("Puntuacion CIMAX Cienzar", "CIMAX Cienzar score"),
        cimaxScore: formatCimaxScore(stats?.cimaxScores?.cienzar || 0),
        wrapperClass: "border-[#2f9658] bg-[#123926]",
        titleClass: "text-[#b8ffd4]",
        metricLabelClass: "text-[#88e8b1]",
      },
      {
        key: "sciencevsazar",
        title: t("Modo Contrazar", "Contrazar mode"),
        cimaxLabel: t("Puntuacion CIMAX Contrazar", "CIMAX Contrazar score"),
        cimaxScore: formatCimaxScore(
          stats?.cimaxScores?.sciencevsazar ||
            stats?.cimaxScores?.contrazar ||
            stats?.cimaxScores?.cimaxContrazar ||
            0
        ),
        wrapperClass: "border-[#b9414c] bg-[#3d1018]",
        titleClass: "text-[#ffc2ca]",
        metricLabelClass: "text-[#ff9ba7]",
      },
      {
        key: "azar",
        title: t("Modo Azar", "Dice mode"),
        cimaxLabel: t("Puntuacion CIMAX Azar", "CIMAX Dice score"),
        cimaxScore: formatCimaxScore(stats?.cimaxScores?.azar || 0),
        wrapperClass: "border-[#b98a1f] bg-[#3d2c10]",
        titleClass: "text-[#ffe2a1]",
        metricLabelClass: "text-[#ffd17a]",
      },
    ].map((column) => {
      const modeStats = modeStatsByApi?.[column.key] || {};
      return {
        ...column,
        metrics: [
          {
            key: "maxForce",
            label: t("Fuerza de juego maxima", "Max game force"),
            value: formatMetricValue(modeStats?.maxGameForceAchieved || 0),
          },
          {
            key: "maxLevel",
            label: t("Nivel maximo de un juego", "Max game level"),
            value: formatMetricValue(modeStats?.maxGameLevelAchieved || 0),
          },
          {
            key: "maxPotency",
            label: t("Potencia maxima", "Max potency"),
            value: formatMetricValue(modeStats?.maxGamePotency || 0),
          },
          {
            key: "avgForce",
            label: t("Fuerza de juego promedio", "Average game force"),
            value: formatMetricValue(modeStats?.averageGameForce || 0),
          },
          {
            key: "avgLevel",
            label: t("Nivel de juego promedio", "Average game level"),
            value: formatMetricValue(modeStats?.averageGameLevel || 0),
          },
          {
            key: "avgPotency",
            label: t("Potencia media", "Average potency"),
            value: formatMetricValue(modeStats?.averageGamePotency || 0),
          },
        ],
      };
    });
  }, [stats, t]);

  const onlineRates = useMemo(() => {
    const fromApi = stats?.onlineModeWinRates || {};
    return [
      {
        key: "free",
        label: "Ciencia",
        value: Number(fromApi?.free?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.free?.matches || 0),
        wins: Number(fromApi?.free?.wins || 0),
        losses: Number(fromApi?.free?.losses || 0),
        draws: Number(fromApi?.free?.draws || 0),
        className: "border-[#3b80d8] bg-[#132c4a] text-[#c4e2ff]",
      },
      {
        key: "cienzar",
        label: "Cienzar",
        value: Number(fromApi?.cienzar?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.cienzar?.matches || 0),
        wins: Number(fromApi?.cienzar?.wins || 0),
        losses: Number(fromApi?.cienzar?.losses || 0),
        draws: Number(fromApi?.cienzar?.draws || 0),
        className: "border-[#2f9658] bg-[#143824] text-[#bfffd6]",
      },
      {
        key: "sciencevsazar",
        label: "Contrazar",
        value: Number(fromApi?.sciencevsazar?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.sciencevsazar?.matches || 0),
        wins: Number(fromApi?.sciencevsazar?.wins || 0),
        losses: Number(fromApi?.sciencevsazar?.losses || 0),
        draws: Number(fromApi?.sciencevsazar?.draws || 0),
        className: "border-[#b9414c] bg-[#3d1018] text-[#ffc2ca]",
      },
      {
        key: "dice",
        label: "Azar",
        value: Number(fromApi?.dice?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.dice?.matches || 0),
        wins: Number(fromApi?.dice?.wins || 0),
        losses: Number(fromApi?.dice?.losses || 0),
        draws: Number(fromApi?.dice?.draws || 0),
        className: "border-[#b98a1f] bg-[#3f2d11] text-[#ffe3a6]",
      },
    ];
  }, [stats]);

  const topCimaxBadges = useMemo(
    () => [
      {
        key: "ciencia",
        label: t("CIMAX CIENCIA", "CIMAX SCIENCE"),
        value: formatCimaxScore(stats?.cimaxScores?.ciencia || 0),
        className: "border-[#3c76c7] bg-[#102a4d] text-[#bfe1ff]",
      },
      {
        key: "cienzar",
        label: t("CIMAX CIENZAR", "CIMAX CIENZAR"),
        value: formatCimaxScore(stats?.cimaxScores?.cienzar || 0),
        className: "border-[#2f9658] bg-[#123a27] text-[#bfffd7]",
      },
      {
        key: "sciencevsazar",
        label: t("CIMAX CONTRAZAR", "CIMAX CONTRAZAR"),
        value: formatCimaxScore(
          stats?.cimaxScores?.sciencevsazar ||
            stats?.cimaxScores?.contrazar ||
            stats?.cimaxScores?.cimaxContrazar ||
            0
        ),
        className: "border-[#b9414c] bg-[#3d1018] text-[#ffc2ca]",
      },
      {
        key: "azar",
        label: t("CIMAX AZAR", "CIMAX RANDOM"),
        value: formatCimaxScore(stats?.cimaxScores?.azar || 0),
        className: "border-[#b98a1f] bg-[#3f2d11] text-[#ffe3a6]",
      },
    ],
    [stats, t]
  );

  const cardsByKey = useMemo(
    () =>
      cards.reduce((accumulator, card) => {
        accumulator[card.key] = card;
        return accumulator;
      }, {}),
    [cards]
  );

  const statsTotals = useMemo(() => {
    const totalMatches = Math.max(0, Number(cardsByKey?.matches?.value) || 0);
    const wins = Math.max(0, Number(cardsByKey?.wins?.value) || 0);
    const losses = Math.max(0, Number(cardsByKey?.losses?.value) || 0);
    const winPercent = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;
    const lossPercent = totalMatches > 0 ? (losses / totalMatches) * 100 : 0;
    return {
      totalMatches,
      wins,
      losses,
      winPercent,
      lossPercent,
      donutStyle: {
        "--xima-stats-win-angle": `${Math.max(0, Math.min(100, winPercent)) * 3.6}deg`,
      },
    };
  }, [cardsByKey]);

  const topCimaxPanels = useMemo(
    () =>
      topCimaxBadges.map((badge) => {
        const modeMeta = {
          ciencia: {
            tone: "is-ciencia",
            icon: IoCubeOutline,
            eyebrow: t("Tu CIMAX actual", "Your current CIMAX"),
          },
          cienzar: {
            tone: "is-cienzar",
            icon: IoBeakerOutline,
            eyebrow: "CIMAX CIENZAR",
          },
          sciencevsazar: {
            tone: "is-sciencevsazar",
            icon: IoSyncOutline,
            eyebrow: "CIMAX CONTRAZAR",
          },
          azar: {
            tone: "is-azar",
            icon: IoDiceOutline,
            eyebrow: "CIMAX AZAR",
          },
        }[badge.key] || {
          tone: "is-ciencia",
          icon: IoCubeOutline,
          eyebrow: badge.label,
        };
        return {
          ...badge,
          tone: modeMeta.tone,
          Icon: modeMeta.icon,
          eyebrow: modeMeta.eyebrow,
        };
      }),
    [topCimaxBadges, t]
  );

  const topMetricPanels = useMemo(
    () => [
      { ...cardsByKey.matches, tone: "is-blue", Icon: IoGameControllerOutline },
      { ...cardsByKey.wins, tone: "is-green", Icon: IoTrophyOutline },
      { ...cardsByKey.losses, tone: "is-red", Icon: IoCloseCircleOutline },
      { ...cardsByKey.avg, tone: "is-purple", Icon: IoTimeOutline },
      { ...cardsByKey.totalTime, tone: "is-cyan", Icon: IoStopwatchOutline },
    ],
    [cardsByKey]
  );

  const accentMetricPanels = useMemo(
    () => [
      { ...cardsByKey.fav, tone: "is-gold", Icon: IoStarOutline },
      { ...cardsByKey.currentStreak, tone: "is-teal", Icon: IoTrendingUpOutline },
      { ...cardsByKey.bestStreak, tone: "is-green", Icon: IoMedalOutline },
      { ...cardsByKey.capturedBlocks, tone: "is-orange", Icon: IoLayersOutline },
      { ...cardsByKey.capturedSpecial, tone: "is-violet", Icon: IoSparklesOutline },
    ],
    [cardsByKey]
  );

  const summaryRows = useMemo(
    () => [
      { key: "currentStreak", label: cardsByKey.currentStreak?.label, value: cardsByKey.currentStreak?.value, Icon: IoTrendingUpOutline, tone: "is-green" },
      { key: "bestStreak", label: cardsByKey.bestStreak?.label, value: cardsByKey.bestStreak?.value, Icon: IoMedalOutline, tone: "is-green" },
      { key: "capturedBlocks", label: cardsByKey.capturedBlocks?.label, value: cardsByKey.capturedBlocks?.value, Icon: IoLayersOutline, tone: "is-orange" },
      { key: "capturedSpecial", label: cardsByKey.capturedSpecial?.label, value: cardsByKey.capturedSpecial?.value, Icon: IoSparklesOutline, tone: "is-violet" },
      { key: "avg", label: cardsByKey.avg?.label, value: cardsByKey.avg?.value, Icon: IoTimeOutline, tone: "is-purple" },
      { key: "totalTime", label: cardsByKey.totalTime?.label, value: cardsByKey.totalTime?.value, Icon: IoStopwatchOutline, tone: "is-cyan" },
    ],
    [cardsByKey]
  );

  const onlineRatesByMode = useMemo(
    () =>
      onlineRates.reduce((accumulator, entry) => {
        const mappedKey =
          entry.key === "free" ? "ciencia" : entry.key === "dice" ? "azar" : entry.key;
        accumulator[mappedKey] = entry;
        return accumulator;
      }, {}),
    [onlineRates]
  );

  const modePerformancePanels = useMemo(
    () =>
      modeColumns.map((column) => {
        const modeMeta = {
          ciencia: { tone: "is-ciencia", Icon: IoCubeOutline },
          cienzar: { tone: "is-cienzar", Icon: IoBeakerOutline },
          sciencevsazar: { tone: "is-sciencevsazar", Icon: IoSyncOutline },
          azar: { tone: "is-azar", Icon: IoDiceOutline },
        }[column.key] || { tone: "is-ciencia", Icon: IoCubeOutline };
        const modeRate = onlineRatesByMode[column.key] || {};
        return {
          ...column,
          ...modeMeta,
          rate: modeRate,
          tournamentWins:
            column.key === "ciencia"
              ? Number(tournamentWinsByMode?.ciencia || 0)
              : column.key === "cienzar"
              ? Number(tournamentWinsByMode?.cienzar || 0)
              : column.key === "sciencevsazar"
              ? Number(tournamentWinsByMode?.sciencevsazar || 0)
              : Number(tournamentWinsByMode?.azar || 0),
        };
      }),
    [modeColumns, onlineRatesByMode, tournamentWinsByMode]
  );

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: "rgb(90, 140, 255)" },
        algorithm: theme.darkAlgorithm,
      }}
    >
      <main className="mobile-landscape-safari-flex-parent xima-stats-page">
        <section className="mobile-landscape-safari-flex-child xima-stats-shell">
          <header className="xima-stats-header">
            <div className="xima-stats-heading">
              <div className="xima-stats-heading-icon">
                <IoStatsChartOutline aria-hidden="true" />
              </div>
              <div>
                <h1>{tKey("ESTADISTICAS")}</h1>
                <p>
                  {t(
                    "Todos los datos corresponden a tu rendimiento general en el juego.",
                    "All data belongs to your overall game performance."
                  )}
                </p>
              </div>
            </div>
            <div className="xima-stats-header-actions">
              <button type="button" onClick={() => navigate("/game/history")}>
                <IoArrowBackOutline aria-hidden="true" />
                {t("Ir a Historial", "Go to History")}
                <IoSyncOutline aria-hidden="true" />
              </button>
              <button type="button" onClick={() => loadStats()} className="is-purple">
                <IoRefreshOutline aria-hidden="true" />
                {tKey("ACTUALIZAR")}
              </button>
            </div>
          </header>

          {loading ? (
            <div className="xima-stats-loading">
              <Spin size="large" />
            </div>
          ) : (
            <div className="xima-stats-content">
              <section className="xima-stats-cimax-grid">
                {topCimaxPanels.map((panel) => {
                  const Icon = panel.Icon;
                  return (
                    <article key={panel.key} className={`xima-stats-cimax-card ${panel.tone}`}>
                      <div className="xima-stats-icon-box">
                        <Icon aria-hidden="true" />
                      </div>
                      <div>
                        <span>{panel.eyebrow}</span>
                        <em>{panel.label}</em>
                        <strong>{panel.value}</strong>
                      </div>
                    </article>
                  );
                })}
                <article className="xima-stats-cimax-card is-purple">
                  <div className="xima-stats-icon-box">
                    <IoFlameOutline aria-hidden="true" />
                  </div>
                  <div>
                    <span>{cardsByKey.dailyStreak?.label}</span>
                    <strong>{cardsByKey.dailyStreak?.value}</strong>
                    <small>{cardsByKey.dailyStreak?.extra}</small>
                  </div>
                </article>
              </section>

              <section className="xima-stats-kpi-strip">
                {topMetricPanels.map((panel) => {
                  const Icon = panel.Icon;
                  return (
                    <article key={panel.key} className={`xima-stats-kpi-card ${panel.tone}`}>
                      <div className="xima-stats-kpi-icon">
                        <Icon aria-hidden="true" />
                      </div>
                      <div>
                        <span>{panel.label}</span>
                        <strong>{panel.value}</strong>
                      </div>
                    </article>
                  );
                })}
              </section>

              <section className="xima-stats-accent-grid">
                {accentMetricPanels.map((panel) => {
                  const Icon = panel.Icon;
                  return (
                    <article key={panel.key} className={`xima-stats-accent-card ${panel.tone}`}>
                      <div>
                        <span>{panel.label}</span>
                        <strong>{panel.value}</strong>
                        {panel.extra ? <small>{panel.extra}</small> : null}
                      </div>
                      <Icon aria-hidden="true" />
                    </article>
                  );
                })}
              </section>

              <div className="xima-stats-main-grid">
                <section className="xima-stats-performance-panel">
                  <div className="xima-stats-section-title">
                    <h2>{t("Rendimiento por modo de juego", "Performance by game mode")}</h2>
                  </div>

                  <div className="xima-stats-mode-grid">
                    {modePerformancePanels.map((panel) => {
                      const Icon = panel.Icon;
                      return (
                        <article key={panel.key} className={`xima-stats-mode-card ${panel.tone}`}>
                          <header>
                            <span>
                              <Icon aria-hidden="true" />
                            </span>
                            <h3>{panel.title}</h3>
                          </header>
                          <div className="xima-stats-mode-score">
                            <span>{panel.cimaxLabel}</span>
                            <strong>{panel.cimaxScore}</strong>
                          </div>
                          <dl className="xima-stats-metric-list">
                            {panel.metrics.map((metric) => (
                              <div key={metric.key}>
                                <dt>{metric.label}</dt>
                                <dd>{metric.value}</dd>
                              </div>
                            ))}
                          </dl>
                          <div className="xima-stats-mode-footer">
                            <div
                              className="xima-stats-ring"
                              style={{
                                "--xima-stats-ring-angle": `${
                                  Math.min(100, Number(panel.tournamentWins || 0)) * 3.6
                                }deg`,
                              }}
                            >
                              <span>{panel.tournamentWins}</span>
                            </div>
                            <div>
                              <span>{t("Torneos ganados", "Tournaments won")}</span>
                              <strong>{panel.tournamentWins}</strong>
                              <small>
                                W {Number(panel.rate?.wins || 0)} · L{" "}
                                {Number(panel.rate?.losses || 0)} · D{" "}
                                {Number(panel.rate?.draws || 0)} · M{" "}
                                {Number(panel.rate?.matches || 0)}
                              </small>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>

                <aside className="xima-stats-side-column">
                  <section className="xima-stats-side-panel">
                    <h2>{t("Distribucion general", "Overall distribution")}</h2>
                    <div className="xima-stats-distribution">
                      <div className="xima-stats-donut" style={statsTotals.donutStyle}>
                        <div>
                          <strong>{statsTotals.totalMatches}</strong>
                          <span>{t("Partidas", "Matches")}</span>
                        </div>
                      </div>
                      <div className="xima-stats-distribution-copy">
                        <div className="is-win">
                          <span />
                          <strong>{t("Victorias", "Wins")}</strong>
                          <em>
                            {statsTotals.wins} ({statsTotals.winPercent.toFixed(1)}%)
                          </em>
                        </div>
                        <div className="is-loss">
                          <span />
                          <strong>{t("Derrotas", "Losses")}</strong>
                          <em>
                            {statsTotals.losses} ({statsTotals.lossPercent.toFixed(1)}%)
                          </em>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="xima-stats-side-panel">
                    <h2>{t("Resumen general", "General summary")}</h2>
                    <div className="xima-stats-summary-list">
                      {summaryRows.map((row) => {
                        const Icon = row.Icon;
                        return (
                          <div key={row.key} className={row.tone}>
                            <Icon aria-hidden="true" />
                            <span>{row.label}</span>
                            <strong>{row.value}</strong>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </aside>
              </div>

              <section className="xima-stats-tournament-band">
                <div>
                  <h2>{t("Torneos ganados por modo", "Tournaments won by mode")}</h2>
                  <div className="xima-stats-tournament-grid">
                    {tournamentWinsCards.map((card) => (
                      <article key={card.key}>
                        <span>{card.label}</span>
                        <strong>{card.value}</strong>
                      </article>
                    ))}
                  </div>
                </div>
                <div className="xima-stats-total-tournaments">
                  <IoPodiumOutline aria-hidden="true" />
                  <span>{t("Total torneos ganados", "Total tournaments won")}</span>
                  <strong>{Number(tournamentWinsByMode?.total || 0)}</strong>
                </div>
              </section>
            </div>
          )}
        </section>
      </main>
    </ConfigProvider>
  );

};

export default Stats;
